// qalaa-pkce-client.mjs
//
// Backend-session client for Qalaa-authenticated applications.
//
// This client does NOT:
// - perform PKCE in the browser
// - exchange authorization codes in JS
// - store tokens in localStorage
// - store tokens in sessionStorage
// - read JWTs from cookies
//
// It assumes:
// - the backend owns the OAuth2 login flow with Qalaa
// - the backend sets a session cookie
// - the frontend checks session state through /api/session/me
// - login is initiated by redirecting to /api/oauth2/authorization/qalaa
//
// Typical endpoints:
//   GET  /api/__session/me
//   GET  /api/oauth2/authorization/backoffice
//   POST /api/logout

export class QalaaPkceClient {
  constructor(options = {}) {
    this.baseUrl = stripTrailingSlash(options.baseUrl || window.location.origin);

    this.loginUrl = absolutizeUrl(
      options.loginUrl || "/api/oauth2/authorization/backoffice",
      this.baseUrl
    );

    this.sessionUrl = absolutizeUrl(
      options.sessionUrl || "/api/__session/me",
      this.baseUrl
    );

    this.logoutUrl = absolutizeUrl(
      options.logoutUrl || "/api/logout",
      this.baseUrl
    );

    this.postLogoutRedirectUri =
      options.postLogoutRedirectUri || window.location.origin;

    this.fetchOptions = {
      credentials: "include",
      cache: "no-store",
      ...options.fetchOptions,
    };

    this.onSession =
      typeof options.onSession === "function" ? options.onSession : () => {};

    this.onLogout =
      typeof options.onLogout === "function" ? options.onLogout : () => {};

    this.onError =
      typeof options.onError === "function" ? options.onError : () => {};

    this._session = null;
    this._ready = false;
    this._refreshInFlight = null;
  }

  async resume() {
    try {
      await this.refreshSession();
    } catch (err) {
      this.onError(err);
      this._session = null;
    } finally {
      this._ready = true;
    }

    return this.getSession();
  }

  isReady() {
    return this._ready;
  }

  isAuthenticated() {
    return !!this._session?.authenticated;
  }

  getSession() {
    return this._session ? { ...this._session } : null;
  }

  getTokens() {
    return null;
  }

  getAccessToken() {
    return null;
  }

  getRefreshToken() {
    return null;
  }

  getAuthorizationHeader() {
    return null;
  }

  async authenticate({
    mode = "redirect",
    returnTo = window.location.href,
    extraParams = {},
  } = {}) {
    if (mode !== "redirect") {
      throw new Error(`Unsupported authenticate mode: ${mode}`);
    }

    const url = new URL(this.loginUrl);

    if (returnTo) {
      url.searchParams.set("return_to", returnTo);
    }

    for (const [k, v] of Object.entries(extraParams || {})) {
      if (v !== undefined && v !== null) {
        url.searchParams.set(k, String(v));
      }
    }

    window.location.assign(url.toString());
    return new Promise(() => {});
  }

  async refresh() {
    return this.refreshSession();
  }

  async refreshSession({ force = false } = {}) {
    if (this._refreshInFlight && !force) {
      return this._refreshInFlight;
    }

    this._refreshInFlight = (async () => {
      try {
        const res = await fetch(this.sessionUrl, {
          method: "GET",
          headers: {
            Accept: "application/json"
          },
          ...this.fetchOptions,
        });

        if (res.status === 401 || res.status === 403) {
          this._session = { authenticated: false };
          this.onSession(this.getSession());
          return this.getSession();
        }

        const payload = await parseJsonSafe(res);

        if (!res.ok) {
          throw new Error(
            `Session request failed: ${res.status} ${res.statusText}`
          );
        }

        this._session = normalizeSessionPayload(payload);
        this.onSession(this.getSession());
        return this.getSession();
      } catch (err) {
        this.onError(err);
        throw err;
      } finally {
        this._refreshInFlight = null;
      }
    })();

    return this._refreshInFlight;
  }

  async ensureAuthenticated() {
    if (this.isAuthenticated()) {
      return this.getSession();
    }

    const session = await this.refreshSession();
    if (session?.authenticated) {
      return session;
    }

    return null;
  }

  async fetchUserInfo() {
    const session = await this.ensureAuthenticated();
    if (!session?.authenticated) {
      throw new Error("Not authenticated");
    }
    return session;
  }

  async authenticatedFetch(input, init = {}) {
    const res = await fetch(input, {
      ...init,
      credentials: "include",
      cache: init.cache || "no-store",
      headers: new Headers(init.headers || {}),
    });

    if (res.status === 401 || res.status === 403) {
      this._session = { authenticated: false };
      this.onSession(this.getSession());
    }

    return res;
  }

  async logout({
    mode = "fetch",
    redirectTo = this.postLogoutRedirectUri,
  } = {}) {
    this._session = null;

    if (mode === "fetch") {
      try {
        await fetch(this.logoutUrl, {
          method: "POST",
          headers: {
            Accept: "application/json"
          },
          credentials: "include",
          cache: "no-store",
        });
      } catch (err) {
        this.onError(err);
      }

      this.onLogout();
      return;
    }

    if (mode === "redirect") {
      const url = new URL(this.logoutUrl);
      if (redirectTo) {
        url.searchParams.set("redirect_to", redirectTo);
      }
      this.onLogout();
      window.location.assign(url.toString());
      return;
    }

    throw new Error(`Unsupported logout mode: ${mode}`);
  }

  async discover() {
    return {
      mode: "backend-session",
      baseUrl: this.baseUrl,
      loginUrl: this.loginUrl,
      sessionUrl: this.sessionUrl,
      logoutUrl: this.logoutUrl,
    };
  }
}

function stripTrailingSlash(s) {
  return String(s).replace(/\/+$/, "");
}

function absolutizeUrl(value, baseUrl) {
  if (!value) {
    return baseUrl;
  }

  try {
    return new URL(value).toString();
  } catch {
    return new URL(value, `${baseUrl}/`).toString();
  }
}

async function parseJsonSafe(response) {
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

function normalizeSessionPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return { authenticated: true };
  }

  if (payload.authenticated === undefined) {
    return {
      authenticated: true,
      ...payload,
    };
  }

  return {
    ...payload,
    authenticated: !!payload.authenticated,
  };
}